import {
  createContext,
  PropsWithChildren,
  ReactElement,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";

import {
  Asset,
  AssetFilter,
  AssetOrder,
  Collection,
  InputMaybe,
} from "../generated/gql/graphql";
import useCollectionAssets from "../hooks/query/asset/use-collection-assets";
import useAssetFilter from "../hooks/useAssetFilter";
import { parseType } from "../utils/graphql";
import { emptyFn } from "../utils/provider";

import AssetListProvider from "./asset-list-provider";

type CollectionAssetsProviderContextType = {
  page: number;
  setPage: (page: number) => void;
  totalCount: number;
  setTotalCount: (count: number) => void;
  limit: number;
  setLimit: (limit: number) => void;
  offset: number;
  setOffset: (offset: number) => void;
  filter: InputMaybe<AssetFilter> | undefined;
  setFilter: (filter: InputMaybe<AssetFilter>) => void;
  orderBy?: AssetOrder | undefined;
  setOrderBy: (orderBy: AssetOrder | undefined) => void;
  collectionAssetsChanged: () => void;
  isLoading: boolean;
  assets: Asset[];
  isError: boolean;
};

export const CollectionAssetsContext =
  createContext<CollectionAssetsProviderContextType>({
    page: 0,
    setPage: emptyFn,
    totalCount: 0,
    setTotalCount: emptyFn,
    limit: 0,
    setLimit: emptyFn,
    offset: 0,
    setOffset: emptyFn,
    filter: undefined,
    setFilter: emptyFn,
    setOrderBy: emptyFn,
    collectionAssetsChanged: emptyFn,

    assets: [],
    isLoading: true,
    isError: false,
  });

type Props = PropsWithChildren & {
  collection: Collection;
};

export default function CollectionAssetsProvider(props: Props): ReactElement {
  const { collection, children } = props;

  const [searchParams] = useSearchParams();

  const [totalCount, setTotalCount] = useState<number>(0);
  const [limit, setLimit] = useState<number>(16);
  const [offset, setOffset] = useState(0);
  const [page, setPage] = useState(parseInt(searchParams.get("page") || "1"));
  const [orderBy, setOrderBy] = useState<AssetOrder | undefined>();
  const { filter, setFilter } = useAssetFilter();

  const [assets, setAssets] = useState<Asset[]>([]);
  const { data, loading, error, refetch } = useCollectionAssets(collection.id, {
    // ToDo: fix type mismatch
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    filter: filter,
    pagination: {
      offset: offset,
      page: page,
      limit: limit,
    },
    orderBy: [
      {
        ...orderBy,
      },
    ],
  });

  function parseData(data: unknown): void {
    if (!data) {
      return;
    }

    const {
      offset: loadedOffset,
      totalCount: loadedTotalCount,
      page: loadedPage,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
    } = data["collectionAssets"];

    setOffset(loadedOffset);
    setTotalCount(loadedTotalCount);
    setPage(loadedPage);
    setAssets(parseType<Asset[]>("collectionAssets", data));
  }

  useEffect(() => {
    parseData(data);
  }, [data]);

  useEffect(() => {
    refetch({
      filter: filter,
      pagination: {
        offset: offset,
        page: page,
        limit: limit,
      },
      orderBy: [
        {
          ...orderBy,
        },
      ],
    });
  }, [filter, offset, page, limit, orderBy]);

  function internalSetFilter(filter: InputMaybe<AssetFilter>): void {
    // Note(john): Reset here all necessary "result values" (e.g. page).
    setPage(1);
    setFilter(filter || undefined);
  }

  function internalSetPage(pageNumber: number): void {
    setPage(pageNumber);
  }

  function internalSetOrder(order: AssetOrder | undefined): void {
    setOrderBy(order);
  }

  const values = useMemo(
    () => ({
      totalCount,
      setTotalCount,
      limit,
      setLimit,
      offset,
      setOffset,
      page,
      setPage: internalSetPage,
      filter,
      setFilter: internalSetFilter,
      orderBy,
      setOrderBy: internalSetOrder,
      collectionAssetsChanged: refetch,

      // Note: Current state and data,
      assets,
      isLoading: loading,
      // ToDo: Add proper error messaging
      isError: error !== undefined,
    }),
    [totalCount, limit, offset, page, filter, orderBy, assets, loading, error]
  );

  return (
    <CollectionAssetsContext.Provider value={values}>
      <AssetListProvider initAssets={assets} collection={collection}>
        {children}
      </AssetListProvider>
    </CollectionAssetsContext.Provider>
  );
}
